import { AcroNode } from "./acrotohtml/types";
import { renderHeader } from "./acrotohtml/renderHeader";
import { renderNode } from "./acrotohtml/renderNode";
import { renderRowsGrid } from "./acrotohtml/renderRowsGrid";
import { renderGroupOptionRow } from "./acrotohtml/renderGroupOptionRow";
import { escapeHtml } from "./acrotohtml/escapeHtml";


export function buildSectionsFromAcro(json: unknown): {
  headersHtml: string;
  sections: { id: string; name: string; html: string }[];
} {
  const root = json as { data?: AcroNode[]; merged?: { data?: AcroNode[] }; children?: AcroNode[] } | undefined;
  // Robust flatten: collect nodes from data, merged.data, children, and any nested arrays one level deeper
  const seeds: (AcroNode | AcroNode[] | undefined)[] = [
    root?.data,
    root?.merged?.data,
    root?.children,
  ];
  const doc: AcroNode[] = [];
  const pushNode = (n: AcroNode) => doc.push(n);
  const pushArray = (arr?: AcroNode[] | null) => {
    (arr ?? []).forEach((n) => pushNode(n));
  };
  seeds.forEach((s) => {
    if (!s) return;
    if (Array.isArray(s)) {
      pushArray(s as AcroNode[]);
    }
  });
  // Some sources may wrap content in a single root group/section with children; include their children as siblings
  if (doc.length === 1 && Array.isArray(doc[0].children) && (doc[0].children as AcroNode[]).length > 0) {
    const only = doc[0];
    doc.splice(0, 1, ...((only.children ?? []) as AcroNode[]));
  }

  const byType = doc.reduce<Record<string, AcroNode[]>>((acc, node) => {
    const t = (node.type || '').toLowerCase();
    (acc[t] || (acc[t] = [])).push(node);
    return acc;
  }, {});
  // Grouping rule with nested-section extraction:
  // - Iterate document order.
  // - When encountering a `section`, start a new group.
  // - If a `section` appears inside another section's children, extract it and
  //   add as a separate sibling group (remove from the parent children list).
  type Grouped = { section: AcroNode; children: AcroNode[] };
  const groupedSections: Grouped[] = [];
  let current: Grouped | null = null;
  const pushGrouped = (sec: AcroNode, children: AcroNode[]) => {
    groupedSections.push({ section: sec, children });
  };
  const extractNestedSections = (nodes: AcroNode[] = []): { kept: AcroNode[]; extracted: Grouped[] } => {
    const kept: AcroNode[] = [];
    const extracted: Grouped[] = [];
    nodes.forEach((child) => {
      const ct = (child.type || '').toLowerCase();
      if (ct === 'section') {
        const nestedChildren = [...((child.children ?? []) as AcroNode[])];
        extracted.push({ section: child, children: nestedChildren });
      } else {
        kept.push(child);
      }
    });
    return { kept, extracted };
  };

  doc.forEach((n) => {
    const t = (n.type || '').toLowerCase();
    if (t === 'section') {
      // Start a new group for this section, extracting any nested sections from its children
      const initialChildren = [...((n.children ?? []) as AcroNode[])];
      const { kept, extracted } = extractNestedSections(initialChildren);
      pushGrouped(n, kept);
      // Append extracted nested sections as sibling groups in the order found
      extracted.forEach((g) => pushGrouped(g.section, g.children));
      current = { section: n, children: kept };
    } else if (current) {
      // Non-section at root: append to current section
      current.children.push(n);
    }
  });
  // Fallback: if we still have no sections in order, collect deep ones (also extracting nested sections)
  if (groupedSections.length === 0) {
    const allCandidates: AcroNode[] = [];
    const collectDeep = (nodes?: AcroNode[] | null) => {
      (nodes ?? []).forEach((n) => {
        if ((n.type || '').toLowerCase() === 'section') {
          allCandidates.push(n);
        }
        if (Array.isArray(n.children) && (n.children as AcroNode[]).length > 0) {
          collectDeep(n.children as AcroNode[]);
        }
      });
    };
    collectDeep(root?.data);
    collectDeep(root?.merged?.data);
    collectDeep(root?.children);
    allCandidates.forEach((s) => {
      const initialChildren = [...((s.children ?? []) as AcroNode[])];
      const { kept, extracted } = extractNestedSections(initialChildren);
      groupedSections.push({ section: s, children: kept });
      extracted.forEach((g) => groupedSections.push(g));
    });
  }

  const sections: { id: string; name: string; html: string }[] = [];
  groupedSections.forEach(({ section: s, children }) => {
    const hasChildren = (children && children.length > 0) || (s.children && s.children.length > 0);
    const rows = (children && children.length > 0) ? children : (s.children ?? []);
    const bodyHtml = hasChildren
      ? `
				<section class="section flex flex-col gap-4" data-section-id="${escapeHtml(String(s.id ?? ''))}">
					${s.title ? `<div class="section-header">${escapeHtml(String(s.title))}</div>` : ''}
					<div class="section-content flex flex-wrap items-center gap-4">
						${rows.map((row) => `<div class="section-row">${renderNode(row)}</div>`).join('')}
					</div>
				</section>
			`
      : renderNode(s);

    sections.push({ id: String(s.id ?? ''), name: String(s.title ?? ''), html: bodyHtml });
  });

  // Build headers HTML based on new rules: consider ONLY the first header
  const firstHeader = (byType['header'] ?? [])[0];
  const headersHtml = renderHeader(firstHeader);

  // Non-section groups (outside headers) also appear as independent blocks above sections
  // Exclude groups that have been wrapped into sections by the grouping rule
  const assignedIds = new Set<string>();
  groupedSections.forEach(g => (g.children ?? []).forEach(c => { if (c.id) assignedIds.add(String(c.id)); }));
  const nonSectionGroups = (byType['group'] ?? []).filter((g) => !g.id || !assignedIds.has(String(g.id)));
  const groupsHtml = nonSectionGroups.map((groupNode) => {
    const children = (groupNode.children ?? []);
    const title = groupNode.title ?? groupNode.sub_title ?? null;
    let showGroupTitle = !!title;
    let normTitle = '';
    if (title) {
      normTitle = String(title).trim().toLowerCase();
      if (normTitle === 'group') {
        showGroupTitle = false;
      }
      if (showGroupTitle && children.length > 0) {
        for (const c of children) {
          if (c.title && String(c.title).trim().toLowerCase() === normTitle) {
            showGroupTitle = false;
            break;
          }
        }
      }
    }
    const safeTitle = title ? escapeHtml(title) : escapeHtml('Group');

    const isOptionsTable = children.length > 0 && children.every((c) => (c.type || '').toLowerCase() === 'group' && (c.options ?? []).length > 0);
    if (isOptionsTable) {
      const headerDesc = groupNode.description ? `<div class="text-xs text-gray-500 mb-2">${escapeHtml(groupNode.description)}</div>` : '';
      const rows = children
        .slice()
        .map((c) => renderGroupOptionRow(c))
        .join('');
      return `
				<div class="flex flex-col gap-4 mb-3">
${showGroupTitle ? `					<div class="text-sm font-medium text-gray-700">${safeTitle}</div>` : ''}
					${headerDesc}
					<div class="divide-y divide-gray-100">${rows}</div>
				</div>
			`;
    }

    if ((groupNode.options ?? []).length > 0) {
      const fieldId = groupNode.field_id ? escapeHtml(String(groupNode.field_id)) : '';
      const fieldIdAttr = fieldId ? ` data-field-id="${fieldId}"` : '';
      const opts = (groupNode.options ?? []).map((opt, i) => `
				<label class="flex items-center gap-2 py-1">
					<input type="checkbox" data-id="${escapeHtml(String(groupNode.id ?? ''))}__opt_${i}"${fieldIdAttr} class="form-checkbox" />
					<span class="text-sm">${escapeHtml(String(opt))}</span>
				</label>
			`).join('');
      const desc = groupNode.description ? `<div class="text-xs text-gray-500 mb-2">${escapeHtml(groupNode.description)}</div>` : '';
      return `
				<div class="flex flex-col gap-4 mb-3">
${showGroupTitle ? `					<div class="text-sm font-medium text-gray-700">${safeTitle}</div>` : ''}
					${desc}
					<div class="divide-y divide-gray-100">${opts || `<div class="text-xs text-gray-400">No options</div>`}</div>
				</div>
			`;
    }

    const rowsHtml = renderRowsGrid(children);
    const desc = groupNode.description ? `<div class="text-xs text-gray-500 mb-2">${escapeHtml(groupNode.description)}</div>` : '';
    return `
			<div class="flex flex-wrap gap-4 mb-3 text-wrapper">
${showGroupTitle ? `				<div class="text-sm font-medium text-gray-700 basis-full">${safeTitle}</div>` : ''}
				${desc}
				${rowsHtml}
			</div>
		`;
  }).join('');

  // Add a header step at the beginning with title 'header'
  const headerStepHtml = (headersHtml + groupsHtml).trim();
  if (headerStepHtml.length > 0) {
    sections.unshift({ id: "__header__", name: "header", html: headerStepHtml });
  }

  return { headersHtml: headersHtml + groupsHtml, sections };
}

export function generateHtmlFromAcro(json: unknown): string {
  const { headersHtml, sections } = buildSectionsFromAcro(json);
  const sectionBodies = sections
    .map((s, idx) => `
			<div class="px-6 py-6" data-step-content="${idx}" style="display: ${idx === 0 ? 'block' : 'none'};">
				<div class="max-w-6xl mx-auto bg-white p-4">
					${s.html}
				</div>
			</div>`)
    .join('');
  const html = `<div class="p-0">${headersHtml}${sectionBodies}</div>`;
  return html;
}
